import React, { useCallback, useRef } from 'react';
import { prop } from 'ramda';

import { Portlet } from 'js/components/system/Portlet';
import { NavigationBar } from 'js/components/main/NavigationBar';

import { useIsAdmin } from 'js/hooks/user';
import { clsx } from 'js/app/utils/clsx.util';
import { useDeviceSelector } from 'js/store/hooks';
import { withPageEvent } from 'js/components/system/Page/hocs';
import { useCurrentItemMenu } from '@interlayer/js/app/hooks/menu/useMenu';
import useSegmentation from 'js/app/hooks/useSegmentation/useSegmentation';
import * as hooks from 'js/components/system/Page/components/Layout/hooks';
import { LayoutLoader } from '@core/js/components/system/Page/components/LayoutLoader';
import { MENU_CLASSES } from '@interlayer/js/app/components/main/DefaultMenu/constants';
import { useEventUpdateDom } from 'js/components/system/Page/buzz/hooks/useEventUpdateDom';
import { useScrollToParagraph } from 'js/components/system/Page/buzz/hooks/useScrollToParagraph';

import style from './Layout.scss';

export const LayoutBare = ({ page, isLoading, isPageInPopup }) => {
    const layoutRef = useRef();
    const isAdmin = useIsAdmin();
    const segmentationFilter = useSegmentation();
    const currentItemMenu = useCurrentItemMenu();
    const formFactor = useDeviceSelector(prop('formFactor'));
    const { portlets, pageLayout, friendlyURL, showAsPopUp } = page;
    const isNavigationBarAvailable = hooks.useIsNavigationBarAvailable(friendlyURL);

    const isShowAsPopUp = !isAdmin && showAsPopUp;

    useScrollToParagraph();
    useEventUpdateDom(layoutRef);

    const getLayout = useCallback((layoutItem, isNavigationBar) => {
        const layout = [];
        const rootLayoutClassName = isNavigationBar ? style.rootLayoutWithNavigation : '';

        if (layoutItem.children?.length) {
            layout.push(
                <div
                    id={layoutItem.id}
                    key={layoutItem.id}
                    style={isNavigationBar ? {} : { width: layoutItem.width }}
                    className={`${style[layoutItem.layoutType]} page-layout-${layoutItem.layoutType} ${rootLayoutClassName}`}
                >
                    {layoutItem.children.map(child => getLayout(child))}
                </div>
            );
        } else {
            layout.push(
                <div
                    id={layoutItem.id}
                    key={layoutItem.id}
                    data-column-id={`column-${layoutItem.dropZoneId}`}
                    style={isNavigationBar ? {} : { width: layoutItem.width }}
                    className={`${style.layout} ${style[`layout-column-${layoutItem.dropZoneId}`]} ${layoutItem.customClasses} ${rootLayoutClassName}`}
                >
                    <div
                        id={`layout-column_column-${layoutItem.dropZoneId}`}
                        className={`portlet-dropzone portlet-column-content fn-portlet-container column-${layoutItem.dropZoneId}`}
                    >
                        {portlets
                            .filter(portlet => isAdmin
                                || Boolean(portlet.enabledDevices[formFactor])
                                && segmentationFilter(portlet.config.segmentation)
                            )
                            .filter(portlet => portlet.column === `column-${layoutItem.dropZoneId}` || portlet.column === layoutItem.dropZoneId)
                            .map(portlet => <Portlet key={portlet.reactPortletId} portlet={portlet}/>)}
                    </div>
                </div>
            );
        }

        return layout;
    }, [formFactor, isAdmin, portlets, segmentationFilter]);

    return (
        <div
            id="main-content"
            ref={layoutRef}
            style={pageLayout.layoutWidth ? { width: pageLayout.layoutWidth } : {}}
            className={clsx('layout-wrapper',
                style.layoutWrapper,
                pageLayout.layoutClasses,
                {
                    isPageBg: currentItemMenu?.cssClasses.includes(MENU_CLASSES.IS_PAGE_BG),
                    [style.admin]: isAdmin,
                    [style.pageInPopup]: isShowAsPopUp
                }
            )}
        >
            {isNavigationBarAvailable() && <NavigationBar currentPageURL={friendlyURL} />}
            {isLoading
                ? <LayoutLoader isPageInPopup={isPageInPopup} />
                : getLayout(pageLayout.layouts[0], isNavigationBarAvailable())
            }
        </div>
    );
};

export const Layout = withPageEvent(LayoutBare);
